import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSendFormMutation } from "../../../api/mutations/send-form.mutations";
import { useGetBrokerDetails } from "../../../api/queries/users.queries";
import { CreateConstructionSiteClientFormData } from "./useCreateConstructionSiteForm";
import { convertUndefinedToNull } from "@dune/utils";
import { buildConstructionSiteClientDto } from "../../../utils/constructionSiteClient.utils";
import { useSelectedBrokerSociety } from "../../../contexts/SelectedBrokerSocietyContext";

const emailSchema = z.object({
	email: z.string().trim().min(1, "Email requis").email("Email invalide"),
});

export type SendQuizToCreateConstructionByClientFormValues = z.infer<
	typeof emailSchema
>;

export const useSendQuizToCreateConstructionByClientDialog = ({
	initialEmail = "",
	onSuccess,
	clientFormData,
}: {
	initialEmail?: string;
	onSuccess?: () => void;
	clientFormData: CreateConstructionSiteClientFormData;
}) => {
	const selectedBrokerSociety = useSelectedBrokerSociety();
	const { data: brokerDetails } = useGetBrokerDetails({
		enabled: !selectedBrokerSociety,
	});
	const sendFormMutation = useSendFormMutation();

	const {
		control,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<SendQuizToCreateConstructionByClientFormValues>({
		resolver: zodResolver(emailSchema),
		defaultValues: { email: initialEmail },
		mode: "onChange",
	});

	const submit: SubmitHandler<
		SendQuizToCreateConstructionByClientFormValues
	> = async (values) => {
		const brokerId = selectedBrokerSociety?.id ?? brokerDetails?.id;

		if (!brokerId) {
			console.error("Broker details not available");
			return;
		}

		try {
			const clientData = buildConstructionSiteClientDto(clientFormData);

			await sendFormMutation.mutateAsync({
				clientEmail: values.email,
				brokerId,
				clientData: convertUndefinedToNull(clientData),
			});
			onSuccess?.();
		} catch (error) {
			console.error("Failed to send form:", error);
		}
	};

	return {
		control,
		errors,
		isSubmitting: sendFormMutation.isPending,
		isValid,
		submit: handleSubmit(submit),
	} as const;
};
